import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, Trash2, Activity, AlertCircle, Square } from 'lucide-react';

import { api, streamPost } from '../lib/api.js';
import { useApiData } from '../lib/hooks.js';
import { useToast } from '../lib/store.js';
import { Card, Button, Spinner, ErrorNote, PageHeader, EmptyState } from '../components/ui.jsx';

// The coach. It can see her programme, her logs and the migraine analysis the
// app worked out, and it is told in no uncertain terms what it must not do —
// no diagnosis, nothing about medication, and no pushing her to eat less.

export default function Coach() {
  const { data, loading, error, reload } = useApiData('/chat');
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [sendError, setSendError] = useState(null);
  const bottomRef = useRef(null);
  const abortRef = useRef(null);
  const { show } = useToast();

  useEffect(() => {
    if (data) setMessages(data.messages);
  }, [data]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: messages.length > 1 ? 'smooth' : 'auto', block: 'end' });
  }, [messages]);

  async function send(text) {
    const question = (text ?? draft).trim();
    if (!question || streaming) return;

    setDraft('');
    setSendError(null);
    setStreaming(true);
    setMessages((m) => [...m, { id: `u${Date.now()}`, role: 'user', content: question }, { id: 'pending', role: 'assistant', content: '' }]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamPost(
        '/chat',
        { message: question },
        (chunk) => {
          setMessages((m) => {
            const next = [...m];
            const last = next[next.length - 1];
            if (last && last.id === 'pending') next[next.length - 1] = { ...last, content: last.content + chunk };
            return next;
          });
        },
        { signal: controller.signal },
      );
      setMessages((m) => m.map((x) => (x.id === 'pending' ? { ...x, id: `a${Date.now()}` } : x)));
    } catch (err) {
      if (err.name === 'AbortError') {
        setMessages((m) => m.filter((x) => x.id !== 'pending' || x.content));
      } else {
        setMessages((m) => m.filter((x) => x.id !== 'pending'));
        setSendError(err);
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }

  if (loading && !data) return <Spinner label="Opening the coach" />;
  if (error) return <div className="p-gutter"><ErrorNote error={error} onRetry={reload} /></div>;

  const notConfigured = data && !data.configured;

  return (
    <div className="max-w-lg mx-auto flex flex-col min-h-screen">
      <PageHeader
        title="Coach"
        subtitle="Ask about today — how you feel, whether to train, what to eat."
        right={
          messages.length > 0 ? (
            <button
              onClick={async () => {
                await api.del('/chat');
                setMessages([]);
                show('Conversation cleared.');
              }}
              className="p-2 text-textMute hover:text-crit"
              aria-label="Clear conversation"
            >
              <Trash2 size={18} />
            </button>
          ) : null
        }
      />

      <div className="px-gutter flex-1 pb-4">
        {notConfigured && (
          <Card className="mb-4 border-warn/40 bg-warnSoft">
            <div className="flex gap-3">
              <AlertCircle size={20} className="text-warn shrink-0 mt-0.5" />
              <div>
                <p className="text-body-sm font-semibold mb-1">The coach is not switched on yet</p>
                <p className="text-meta text-textMute">
                  It needs an API key on the Pages project. Everything else in the app works without it, including the migraine
                  patterns.
                </p>
              </div>
            </div>
          </Card>
        )}

        <Card className="mb-4">
          <div className="flex items-center gap-3">
            <Activity size={18} className="text-accent shrink-0" />
            <p className="text-body-sm text-textSoft flex-1">Migraine patterns, worked out from your own logs.</p>
            <Link to="/migraines" className="text-body-sm text-accent font-semibold shrink-0">Open</Link>
          </div>
        </Card>

        {messages.length === 0 && (
          <>
            <EmptyState
              title="How are you doing today?"
              body="It knows your programme, this week's sessions, what you have logged and what your migraines have lined up with so far."
            />
            <div className="grid grid-cols-2 gap-2 mt-2">
              {(data?.starters || []).map((s) => (
                <button
                  key={s.label}
                  onClick={() => send(s.text)}
                  disabled={notConfigured}
                  className="p-3 rounded-control border border-edge bg-panel text-left text-body-sm hover:border-edgeHi transition-colors disabled:opacity-40"
                >
                  {s.label}
                </button>
              ))}
            </div>
          </>
        )}

        <div className="space-y-3">
          {messages.map((m) => (
            <Bubble key={m.id} role={m.role} content={m.content} pending={m.id === 'pending' && !m.content} />
          ))}
        </div>

        {sendError && (
          <div className="mt-3">
            <ErrorNote error={sendError} />
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Sits above the tab bar. */}
      <div
        className="sticky bottom-0 bg-bg/95 backdrop-blur border-t border-edge px-gutter py-3"
        style={{ marginBottom: 'calc(env(safe-area-inset-bottom, 0px) + 64px)' }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="flex gap-2 items-end"
        >
          <textarea
            rows={1}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={notConfigured ? 'Needs an API key first' : 'How are you feeling?'}
            disabled={notConfigured}
            aria-label="Message the coach"
            className="field py-2.5 resize-none max-h-32 flex-1"
            style={{ height: 'auto' }}
          />
          {streaming ? (
            <button
              type="button"
              onClick={() => abortRef.current?.abort()}
              className="btn-secondary px-3 py-2.5 shrink-0"
              aria-label="Stop"
            >
              <Square size={16} />
            </button>
          ) : (
            <button type="submit" disabled={!draft.trim() || notConfigured} className="btn-primary px-3 py-2.5 shrink-0" aria-label="Send">
              <Send size={16} />
            </button>
          )}
        </form>
        <p className="text-meta text-textMute mt-2">
          Not a doctor. Anything about medication, diagnosis or your fertility treatment is one for your GP or clinic.
        </p>
      </div>
    </div>
  );
}

function Bubble({ role, content, pending }) {
  const mine = role === 'user';
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-card px-3.5 py-2.5 ${
          mine ? 'bg-accent text-onAccent' : 'bg-panel border border-edge'
        }`}
      >
        {pending ? (
          <span className="flex gap-1 py-1" aria-label="Thinking">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="w-1.5 h-1.5 rounded-full bg-textMute animate-pulse"
                style={{ animationDelay: `${i * 160}ms` }}
              />
            ))}
          </span>
        ) : (
          <p className="text-body-sm whitespace-pre-wrap">{content}</p>
        )}
      </div>
    </div>
  );
}
